/**
 * @file BrandModeList.tsx
 * @description Состав бренда в шапке (phase19.16 Р1; SB90, SB21;
 * uiux-design-r1 §2): «Логотип + название + слоган», «Логотип + название»,
 * «Только логотип», «Только название». Вариант без нужного значения
 * неактивен и ведёт задать его: «Слоган — не задан [Задать →]».
 */

import type { SiteHeaderBrandMode } from "@chaiberi/shared";
import { Link } from "react-router";

import { BRAND_MODE_META } from "../../../lib/site-view/header-brand-modes";
import type { SiteBrandDraft } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

/** Подпись варианта и чего ему не хватает (общий реестр со сводкой группы). */
const MODE_META = BRAND_MODE_META;

/** Порядок вариантов по макету: от самого полного к самому краткому. */
const BRAND_MODE_ORDER = Object.keys(MODE_META) as SiteHeaderBrandMode[];

const NEED_TEXT: Readonly<Record<"logo" | "tagline", string>> = {
  logo: "логотип не загружен",
  tagline: "слоган не задан",
};

interface Props {
  value: SiteHeaderBrandMode;
  brand: SiteBrandDraft;
  settingsHref: string;
  onChange: (value: SiteHeaderBrandMode) => void;
}

export function BrandModeList({ value, brand, settingsHref, onChange }: Props) {
  const has = { logo: brand.logoLight !== null || brand.logoDark !== null, tagline: !!brand.tagline?.trim() };
  return (
    <div role="radiogroup" aria-label="Бренд в шапке" className="flex flex-col">
      {BRAND_MODE_ORDER.map((mode) => {
        const meta = MODE_META[mode];
        const missing = meta.needs.filter((n) => !has[n]);
        const active = mode === value;
        return (
          <div key={mode} className="flex min-h-12 flex-wrap items-center gap-x-3">
            <button
              type="button"
              role="radio"
              aria-checked={active}
              disabled={missing.length > 0 && !active}
              onClick={() => onChange(mode)}
              className="flex min-h-12 items-center gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              <span
                aria-hidden
                className={cn(
                  "inline-block size-4 shrink-0 rounded-full [box-shadow:inset_0_0_0_1.5px_var(--outline-variant)]",
                  active && "[box-shadow:inset_0_0_0_5px_var(--primary)]",
                )}
              />
              <span className="type-body text-on-surface">{meta.label}</span>
            </button>
            {missing.length > 0 ? (
              <span className="type-caption text-on-surface-variant">
                {missing.map((m) => NEED_TEXT[m]).join(", ")} ·{" "}
                <Link to={settingsHref} className="font-medium text-primary hover:underline">
                  Задать →
                </Link>
              </span>
            ) : null}
          </div>
        );
      })}
      <p className="type-caption text-on-surface-variant">На телефоне слоган скрывается сам.</p>
    </div>
  );
}
